import {
  Component,
  ChangeDetectionStrategy,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import { CommonModule } from '@angular/common';
import { firstValueFrom } from 'rxjs';

import { BaseModalComponent } from '../../modals/base-modal/base-modal.component';
import { PdfViewerComponent, PageRenderedEvent } from '../project-annotator/pdf-viewer.component';
import { AnnotationOverlayDirective } from '../project-annotator/annotation-overlay.directive';

import { PDF_SOURCE } from '../../../claim-annotator/ports/pdf-source.port';
import { InMemoryPdfSource } from '../../../claim-annotator/adapters/in-memory-pdf-source';
import {
  StaticOverlayAnnotationSource,
  StaticOverlayClaimSource,
} from '../../../claim-annotator/domain/overlay-source';
import { AnnotationRow, Claim, PageGeometry } from '../../../claim-annotator/domain/types';
import {
  ClaimAnnotationDto,
  ConflictReviewer,
  DataExtractionConflictService,
} from '../../../services/data-extraction-conflict.service';

/** Fixed, colorblind-safe colors for the two reviewers (blue / orange, not red/green). */
export const REVIEWER_A_COLOR = '#1f77b4';
export const REVIEWER_B_COLOR = '#ff7f0e';

/**
 * Read-only modal that overlays two reviewers' evidence boxes for a single
 * question on one PDF. Each reviewer's boxes are recolored to a fixed color and
 * filtered to the question's claim id. A reviewer with no box for the question is
 * flagged as having no evidence.
 */
@Component({
  selector: 'app-reviewer-annotations-modal',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, BaseModalComponent, PdfViewerComponent, AnnotationOverlayDirective],
  providers: [InMemoryPdfSource, { provide: PDF_SOURCE, useExisting: InMemoryPdfSource }],
  templateUrl: './reviewer-annotations-modal.component.html',
})
export class ReviewerAnnotationsModalComponent implements OnChanges {
  private conflictService = inject(DataExtractionConflictService);
  private pdfSource = inject(InMemoryPdfSource);

  @Input() open = false;
  @Output() openChange = new EventEmitter<boolean>();
  @Input() projectId!: number;
  @Input() paperId!: number;
  /** The shared document key, e.g. `paper:{paperId}`. */
  @Input() documentId!: string;
  /** PDF blob resolved by the parent before opening. */
  @Input() pdf!: Blob;
  @Input() reviewerA!: ConflictReviewer;
  @Input() reviewerB!: ConflictReviewer;
  /** The claim id to filter to, e.g. `q:{questionId}`. */
  @Input() claimFilter!: string;
  @Input() questionText = '';
  @Input() claims: Claim[] = [];

  readonly REVIEWER_A_COLOR = REVIEWER_A_COLOR;
  readonly REVIEWER_B_COLOR = REVIEWER_B_COLOR;

  readonly sourceA = new StaticOverlayAnnotationSource();
  readonly sourceB = new StaticOverlayAnnotationSource();
  readonly claimSource = new StaticOverlayClaimSource();

  readonly viewer = viewChild<PdfViewerComponent>('viewer');
  // Per-layer jump highlight; the seq nonce lets a repeat jump to the same box replay the pulse.
  readonly highlightA = signal<{ id: string; seq: number } | null>(null);
  readonly highlightB = signal<{ id: string; seq: number } | null>(null);
  private highlightSeq = 0;

  // Page geometry/elements collected from the viewer, same maps as the annotator page.
  readonly pageGeometries = signal<Map<number, PageGeometry>>(new Map());
  readonly pageElements = signal<Map<number, HTMLElement>>(new Map());
  readonly pages = computed(() => Array.from(this.pageGeometries().keys()).sort((a, b) => a - b));
  readonly loadFailed = signal(false);
  readonly loading = signal(false);
  readonly aHasBoxes = signal(false);
  readonly bHasBoxes = signal(false);

  private loaded = false;

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['open']) {
      if (this.open && !this.loaded) {
        void this.load();
      } else if (!this.open) {
        // Reset so reopening (e.g. for another question) reloads fresh.
        this.loaded = false;
        this.highlightA.set(null);
        this.highlightB.set(null);
      }
    }
  }

  private async load(): Promise<void> {
    this.loaded = true;
    this.loadFailed.set(false);
    this.claimSource.set(this.claims);
    this.pdfSource.setBlob(this.pdf, `${this.paperId}.pdf`);
    this.loading.set(true);
    try {
      const [a, b] = await Promise.all([
        firstValueFrom(
          this.conflictService.getReviewerAnnotations(
            this.projectId,
            this.paperId,
            this.reviewerA.reviewerId,
          ),
        ),
        firstValueFrom(
          this.conflictService.getReviewerAnnotations(
            this.projectId,
            this.paperId,
            this.reviewerB.reviewerId,
          ),
        ),
      ]);
      this.sourceA.set(a.map((d) => this.toRow(d, this.REVIEWER_A_COLOR)));
      this.sourceB.set(b.map((d) => this.toRow(d, this.REVIEWER_B_COLOR)));
      this.aHasBoxes.set(a.some((x) => x.claimRef === this.claimFilter));
      this.bHasBoxes.set(b.some((x) => x.claimRef === this.claimFilter));
    } finally {
      this.loading.set(false);
    }
  }

  private toRow(dto: ClaimAnnotationDto, color: string): AnnotationRow {
    return {
      id: dto.id,
      document_id: this.documentId,
      claim_id: dto.claimRef,
      type: dto.type as AnnotationRow['type'],
      text: dto.text || undefined,
      note: dto.note || undefined,
      ocr_text: dto.ocrText || undefined,
      page: dto.page,
      x: dto.x,
      y: dto.y,
      width: dto.width,
      height: dto.height,
      color,
      session_id: dto.sessionId,
      created_at: dto.createdAt,
    };
  }

  onPageRendered(ev: PageRenderedEvent): void {
    const vp = ev.viewport as {
      convertToPdfPoint(x: number, y: number): [number, number];
      convertToViewportPoint(x: number, y: number): [number, number];
      viewBox: [number, number, number, number];
    };
    const geom: PageGeometry = {
      pageWidthPdf: vp.viewBox[2] - vp.viewBox[0],
      pageHeightPdf: vp.viewBox[3] - vp.viewBox[1],
      toPdfPoint: (s) => {
        const [x, y] = vp.convertToPdfPoint(s.x, s.y);
        return { x, y };
      },
      toScreenPoint: (p) => {
        const [x, y] = vp.convertToViewportPoint(p.x, p.y);
        return { x, y };
      },
    };
    this.pageGeometries.update((m) => {
      const next = new Map(m);
      next.set(ev.pageNumber, geom);
      return next;
    });
    this.pageElements.update((m) => {
      const next = new Map(m);
      next.set(ev.pageNumber, ev.pageElement);
      return next;
    });
  }

  geometryFor(page: number): PageGeometry | null {
    return this.pageGeometries().get(page) ?? null;
  }
  pageElementFor(page: number): HTMLElement | null {
    return this.pageElements().get(page) ?? null;
  }

  onViewerLoadFailed(): void {
    this.loadFailed.set(true);
  }

  /** First box = lowest page, then topmost on that page (greatest top edge, PDF y grows up). */
  private firstBox(rows: AnnotationRow[]): AnnotationRow | null {
    const filtered = rows.filter((r) => r.claim_id === this.claimFilter);
    if (filtered.length === 0) return null;
    return filtered.reduce((best, r) => {
      if (r.page !== best.page) return r.page < best.page ? r : best;
      return r.y + r.height > best.y + best.height ? r : best;
    });
  }

  jumpToFirstBox(reviewer: 'A' | 'B'): void {
    // Gate on the first rendered page, not just the annotation fetch: the viewer's scroll API
    // dereferences page DOM and throws while the document is still parsing. Same readiness
    // criterion as the jump buttons' [disabled] binding.
    if (this.loading() || this.loadFailed() || this.pages().length === 0) return;
    const source = reviewer === 'A' ? this.sourceA : this.sourceB;
    const box = this.firstBox(source.annotations());
    if (!box) return;
    // Use the target page's geometry when rendered; any page's height is a close stand-in
    // (uniform page sizes are the norm).
    const geom =
      this.pageGeometries().get(box.page) ?? this.pageGeometries().values().next().value!;
    const topEdge = box.y + box.height;
    const fromTop = ((geom.pageHeightPdf - topEdge) / geom.pageHeightPdf) * 100;
    const topPercent = Math.max(0, Math.min(100, fromTop - 3));
    this.viewer()?.scrollTo(box.page, topPercent);
    const highlight = { id: box.id, seq: ++this.highlightSeq };
    this.highlightA.set(reviewer === 'A' ? highlight : null);
    this.highlightB.set(reviewer === 'B' ? highlight : null);
  }

  close(): void {
    this.open = false;
    this.highlightA.set(null);
    this.highlightB.set(null);
    this.openChange.emit(false);
  }
}
